/**
 * Hub transversal Datos Maestros — tarjetas filtradas por entitlement comercial.
 * Fase 1: catálogo fiscal compartido + accesos SAT sin mover el motor de sync.
 */
import { hasEntitlement } from "@/modules/entitlementsFromSystemInfo";
import { VALIDADOR_DATOS_CHECK_ID_LABEL } from "@/lib/checkIdLabels";

export const DATOS_MAESTROS_PATH = "/datos-maestros";

/** Ruta canónica del validador Check ID (Datos Maestros). */
export const DATOS_MAESTROS_CHECK_ID_PATH = "/datos-maestros/validador-check-id";

/** Ruta canónica — validación RFC/CURP asistida ante portal SAT. */
export const DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH = "/datos-maestros/validacion-rfc-sat";

/** @typedef {'any' | 'all'} EntitlementMode */

/**
 * @typedef {Object} DatosMaestrosCardDef
 * @property {string} id
 * @property {string} path
 * @property {string} label
 * @property {string} description
 * @property {string} iconKey
 * @property {string} [section]
 * @property {string[]} requiredEntitlements
 * @property {EntitlementMode} [entitlementMode]
 * @property {string} testid
 * @property {'real' | 'pending'} [status]
 * @property {boolean} [designerOnly] - Solo visible para platform_designer
 */

/** @type {DatosMaestrosCardDef[]} */
export const DATOS_MAESTROS_CARDS = [
  {
    id: "empresas-clientes",
    path: "/clientes",
    label: "Empresas / Clientes",
    description:
      "Alta y administración del catálogo fiscal compartido: RFC, contacto, certificados FIEL y datos por contribuyente.",
    iconKey: "clients",
    section: "Catálogo fiscal compartido",
    requiredEntitlements: ["visor", "laboral", "tramites", "despacho"],
    entitlementMode: "any",
    testid: "datos-maestros-card-clientes",
    status: "real",
  },
  {
    id: "csf-opinion",
    path: "/documentos-fiscales-sat",
    label: "CSF y opinión SAT",
    description:
      "Constancia de situación fiscal y opinión de cumplimiento con la e.firma del cliente (pantalla canónica del Visor SAT).",
    iconKey: "validate",
    section: "Documentos fiscales SAT",
    requiredEntitlements: ["visor"],
    testid: "datos-maestros-card-csf",
    status: "real",
  },
  {
    id: "descarga-cfdi",
    path: "/descarga-automatica-sat",
    label: "Descarga de CFDI SAT",
    description:
      "Rescate manual de CFDI emitidos y recibidos por periodo con la e.firma del cliente.",
    iconKey: "cloud",
    section: "Integración SAT",
    requiredEntitlements: ["visor"],
    testid: "datos-maestros-card-descarga-sat",
    status: "real",
  },
  {
    id: "monitor-sync",
    path: "/monitor-sincronizacion-sat",
    label: "Estado sincronización SAT",
    description:
      "Consulte la bitácora de descarga automática, reintentos y resumen de CFDI almacenados por cliente.",
    iconKey: "cloud",
    section: "Integración SAT",
    requiredEntitlements: ["visor"],
    testid: "datos-maestros-card-monitor-sync",
    status: "real",
    designerOnly: true,
  },
  {
    id: "check-id-laboral",
    path: DATOS_MAESTROS_CHECK_ID_PATH,
    label: VALIDADOR_DATOS_CHECK_ID_LABEL,
    description:
      "RFC/CURP, NSS e IMSS — consistencia y preparación para alta e inscripción del trabajador (Check ID laboral).",
    iconKey: "validate",
    section: "Validación laboral",
    requiredEntitlements: ["laboral"],
    testid: "datos-maestros-card-check-id-laboral",
    status: "real",
  },
  {
    id: "validacion-rfc-sat",
    path: DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
    label: "Validación de RFC SAT",
    description:
      "Validación individual o masiva de RFC/CURP contra el portal del SAT (flujo asistido con layout y respuesta).",
    iconKey: "validate",
    section: "Validación fiscal",
    requiredEntitlements: ["tramites", "laboral"],
    entitlementMode: "any",
    testid: "datos-maestros-card-validacion-rfc-sat",
    status: "real",
  },
  {
    id: "auditoria-efos",
    path: "/auditoria-efos",
    label: "Auditoría CFDI (EFOS)",
    description:
      "Revise listas EFOS/69-B y el riesgo fiscal de emisores y receptores en sus CFDI.",
    iconKey: "shield",
    section: "Auditoría y cumplimiento",
    requiredEntitlements: ["visor"],
    testid: "datos-maestros-card-auditoria-efos",
    status: "real",
  },
  {
    id: "auditoria-estatus-local",
    path: "/auditoria-estatus-local",
    label: "Auditoría estatus local",
    description:
      "Detecte CFDI marcados cancelados en la copia local sin confirmar en SAT y verifíquelos en lote.",
    iconKey: "shield",
    section: "Auditoría y cumplimiento",
    requiredEntitlements: ["visor"],
    testid: "datos-maestros-card-auditoria-estatus-local",
    status: "real",
  },
];

/**
 * @param {DatosMaestrosCardDef} card
 * @param {object} entitlementsState
 * @param {{ isPlatformDesigner?: boolean }} [opts]
 */
export function isDatosMaestrosCardEntitled(card, entitlementsState, { isPlatformDesigner = false } = {}) {
  if (card?.designerOnly && !isPlatformDesigner) return false;
  if (isPlatformDesigner) return true;
  const reqs = card?.requiredEntitlements || [];
  if (!reqs.length) return true;
  const mode = card.entitlementMode || "any";
  const check = (id) => hasEntitlement(id, entitlementsState, { isPlatformDesigner });
  if (mode === "all") {
    return reqs.every(check);
  }
  return reqs.some(check);
}

/**
 * @param {DatosMaestrosCardDef[]} [cards]
 * @param {object} entitlementsState
 * @param {{ isPlatformDesigner?: boolean }} [opts]
 */
export function filterDatosMaestrosCards(cards, entitlementsState, opts = {}) {
  return (cards || DATOS_MAESTROS_CARDS).filter((card) =>
    isDatosMaestrosCardEntitled(card, entitlementsState, opts),
  );
}

/** Agrupa tarjetas the hub por sección (orden estable). */
export function groupDatosMaestrosCards(cards) {
  const sections = [];
  const byKey = new Map();
  for (const card of cards || []) {
    const key = card.section || "";
    if (!byKey.has(key)) {
      byKey.set(key, []);
      sections.push({ key, label: key || null, items: byKey.get(key) });
    }
    byKey.get(key).push(card);
  }
  return sections;
}
